import type * as LabelPrimitive from "@radix-ui/react-label"
import { Slot } from "@radix-ui/react-slot"
import type { AnyFieldApi } from "@tanstack/form-core"
import type { ReactFormExtendedApi } from "@tanstack/react-form"
import * as React from "react"
import { cn } from "../../lib/utils"
import { Label } from "./label"

type FormContextValue = ReactFormExtendedApi<
	any,
	any,
	any,
	any,
	any,
	any,
	any,
	any,
	any,
	any,
	any,
	any
>

const FormContext = React.createContext<FormContextValue | null>(null)

type FormProps<TForm extends FormContextValue> = {
	form: TForm
	children: React.ReactNode
}

const Form = <TForm extends FormContextValue>({
	form,
	children,
}: FormProps<TForm>) => {
	return <FormContext.Provider value={form}>{children}</FormContext.Provider>
}

type FormFieldContextValue = {
	name: string
	field: AnyFieldApi
	state: AnyFieldApi["state"]
	form: FormContextValue
}

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null)

type RenderFieldProps = {
	field: {
		name: string
		value: unknown
		onChange: (
			event: React.ChangeEvent<
				HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
			>,
		) => void
		onBlur: () => void
		setValue: (value: unknown) => void
	}
	fieldApi: AnyFieldApi
	state: AnyFieldApi["state"]
	form: FormContextValue
}

type FormFieldProps = {
	name: string
	render?: (props: RenderFieldProps) => React.ReactNode
	children?: (props: RenderFieldProps) => React.ReactNode
}

const getEventValue = (
	event: React.ChangeEvent<
		HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
	>,
) => {
	const target = event.target as
		| HTMLInputElement
		| HTMLTextAreaElement
		| HTMLSelectElement
	if (target.type === "checkbox") {
		return (target as HTMLInputElement).checked
	}

	if (target.type === "radio") {
		return (target as HTMLInputElement).value
	}

	return target.value
}

const FormField = ({ name, render, children }: FormFieldProps) => {
	const form = React.useContext(FormContext)

	if (!form) {
		throw new Error("FormField must be used within a <Form> provider")
	}

	const renderer = render ?? children

	if (!renderer) {
		throw new Error("FormField requires a render or child function")
	}

	return (
		<form.Field name={name}>
			{(fieldApi) => {
				const state = fieldApi.state
				const safeValue =
					state.value === undefined || state.value === null ? "" : state.value

				const contextValue: FormFieldContextValue = {
					name,
					field: fieldApi,
					state,
					form,
				}

				return (
					<FormFieldContext.Provider value={contextValue}>
						{renderer({
							field: {
								name,
								value: safeValue,
								onBlur: fieldApi.handleBlur,
								onChange: (event) =>
									fieldApi.handleChange(getEventValue(event) as never),
								setValue: (nextValue) =>
									fieldApi.handleChange(nextValue as never),
							},
							fieldApi,
							state,
							form,
						})}
					</FormFieldContext.Provider>
				)
			}}
		</form.Field>
	)
}

type FormItemContextValue = {
	id: string
}

const FormItemContext = React.createContext<FormItemContextValue | null>(null)

const useFormField = () => {
	const fieldContext = React.useContext(FormFieldContext)
	const itemContext = React.useContext(FormItemContext)

	if (!fieldContext) {
		throw new Error("useFormField should be used within <FormField>")
	}

	if (!itemContext) {
		throw new Error("useFormField should be used within <FormItem>")
	}

	const { id } = itemContext
	const { name, state } = fieldContext

	const firstError = state.meta.errors?.[0]
	const errorMessage =
		typeof firstError === "string"
			? firstError
			: typeof firstError === "object" &&
					firstError !== null &&
					"message" in firstError
				? String(
						// biome-ignore lint/suspicious/noExplicitAny: zod issues expose a message property
						(firstError as Record<string, any>).message ?? firstError,
					)
				: firstError != null
					? String(firstError)
					: null

	return {
		id,
		name,
		state,
		error: errorMessage,
		formItemId: `${id}-form-item`,
		formDescriptionId: `${id}-form-item-description`,
		formMessageId: `${id}-form-item-message`,
	}
}

function FormItem({ className, ...props }: React.ComponentProps<"div">) {
	const id = React.useId()

	return (
		<FormItemContext.Provider value={{ id }}>
			<div
				data-slot="form-item"
				className={cn("grid gap-2", className)}
				{...props}
			/>
		</FormItemContext.Provider>
	)
}

function FormLabel({
	className,
	...props
}: React.ComponentProps<typeof LabelPrimitive.Root>) {
	const { error, formItemId } = useFormField()

	return (
		<Label
			data-slot="form-label"
			data-error={!!error}
			className={cn("data-[error=true]:text-destructive", className)}
			htmlFor={formItemId}
			{...props}
		/>
	)
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
	const { error, formItemId, formDescriptionId, formMessageId } = useFormField()

	return (
		<Slot
			data-slot="form-control"
			id={formItemId}
			aria-describedby={
				!error
					? `${formDescriptionId}`
					: `${formDescriptionId} ${formMessageId}`
			}
			aria-invalid={!!error}
			{...props}
		/>
	)
}

function FormDescription({ className, ...props }: React.ComponentProps<"p">) {
	const { formDescriptionId } = useFormField()

	return (
		<p
			data-slot="form-description"
			id={formDescriptionId}
			className={cn("text-muted-foreground text-sm", className)}
			{...props}
		/>
	)
}

function FormMessage({ className, ...props }: React.ComponentProps<"p">) {
	const { error, formMessageId } = useFormField()
	const body = error ?? props.children

	if (!body) {
		return null
	}

	return (
		<p
			data-slot="form-message"
			id={formMessageId}
			className={cn("text-destructive text-sm font-medium", className)}
			{...props}
		>
			{body}
		</p>
	)
}

export {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	useFormField,
}
